import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import Swal from 'sweetalert2';
import Pagination from '../generic-ui/Pagination';
import SimpleSelect from '../generic-ui/Select';
import {
  startLoadingInterestDetail,
  startLoadingInterestsPaginated,
  startDownloadSupportInboxExcel,
  startUpdateSupportStatus,
  cleanUserInterestSelection,
} from '../../app/actions/userInterestActions';
import { startLoadIntegrationModuleRecord } from '../../app/actions/semanticIntegrationActions';
import { isSupportInboxExcelExportEnabled } from '../../app/helpers/flowEngineConfigHelper';
import { getInboxRecordPath } from '../end-user-search/moduleIntegrationMeta';
import {
  formatSupportAnswerLabel,
  formatSupportAnswerValue,
  listSupportAnswers,
} from '../end-user-search/supportAnswerDisplay';

const listOptions = [5, 10, 15, 20, 25, 50];

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '94%',
    maxWidth: '760px',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  overlay: { backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 3200 },
};

const MODULE_KEYS = [
  'technology',
  'alternative',
  'experience',
  'innovation',
  'assistance',
  'financial',
  'vision',
  'regulation',
];

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_e) {
    return String(value);
  }
};

const formatOrigins = (row) => {
  if (Array.isArray(row.origins) && row.origins.length) {
    return row.origins
      .map((o) => `${o.module_key || ''} #${o.id}${o.name ? ` (${o.name})` : ''}`.trim())
      .join('; ');
  }
  const ids = Array.isArray(row.origin_reference_ids)
    ? row.origin_reference_ids.join(', ')
    : (row.origin_reference_id || '');
  return `${row.bridge_kind || '—'} ${ids}`.trim();
};

const SpecialistSupportInbox = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    interestsP,
    totalPagesP,
    totalItemsP,
    currentPageP,
    selectedInterest,
    selectedOrigins,
    selectedTarget,
    loading,
  } = useSelector((state) => state.userInterest);
  const roleLogin = useSelector((state) => state.userRole.roleLogin);
  const configuration = useSelector((state) => state.configuration);
  const excelExportEnabled = isSupportInboxExcelExportEnabled(configuration);

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    module_key: '',
    bridge_kind: '',
    status: '',
    from: '',
    to: '',
  });
  const [detailOpen, setDetailOpen] = useState(false);
  const [canWriteServer, setCanWriteServer] = useState(null);
  const [exporting, setExporting] = useState(false);

  const canWriteClient = !!(
    roleLogin?.isAdministrator ||
    roleLogin?.isSpecialistManager ||
    roleLogin?.isSpecialistValidator ||
    roleLogin?.isSpecialistEditor
  );
  const canWrite = canWriteServer === null ? canWriteClient : canWriteServer;

  const inboxFilters = () => ({ ...filters, scope: 'inbox' });

  const loadPage = async (page) => {
    const body = await dispatch(startLoadingInterestsPaginated(page, itemsPerPage, inboxFilters()));
    if (body && body.inbox && typeof body.inbox.canWrite === 'boolean') {
      setCanWriteServer(body.inbox.canWrite);
    }
  };

  useEffect(() => {
    loadPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onApplyFilters = (e) => {
    e.preventDefault();
    loadPage(0);
  };

  const onPageChange = (pageClick) => {
    loadPage(pageClick - 1);
  };

  const onChangeSelect = (event) => {
    const size = Number(event.target.value);
    setItemsPerPage(size);
    dispatch(startLoadingInterestsPaginated(0, size, inboxFilters())).then((body) => {
      if (body && body.inbox && typeof body.inbox.canWrite === 'boolean') {
        setCanWriteServer(body.inbox.canWrite);
      }
    });
  };

  const onOpenDetail = async (row) => {
    await dispatch(startLoadingInterestDetail(row.id));
    setDetailOpen(true);
  };

  const onCloseDetail = () => {
    setDetailOpen(false);
    dispatch(cleanUserInterestSelection());
  };

  const moduleLabel = (key) =>
    t(`semanticSearch.module.${key}`, { defaultValue: key || '—' });

  const openRecord = async (moduleKey, id) => {
    if (!moduleKey || !id) {
      Swal.fire(
        t('titles.error'),
        t('semanticSearch.inbox.cannotOpen', { defaultValue: 'No hay ficha para abrir.' }),
        'warning'
      );
      return;
    }
    const body = await dispatch(startLoadIntegrationModuleRecord(moduleKey, id));
    if (!body || !body.ok) {
      Swal.fire(
        t('titles.error'),
        (body && body.msg) || t('semanticSearch.inbox.cannotOpen', { defaultValue: 'No hay ficha para abrir.' }),
        'error'
      );
      return;
    }
    navigate(`${getInboxRecordPath(moduleKey)}?from=support-inbox`);
  };

  const onExportExcel = async () => {
    setExporting(true);
    try {
      await dispatch(startDownloadSupportInboxExcel(inboxFilters()));
    } finally {
      setExporting(false);
    }
  };

  const onChangeStatus = async (id, status) => {
    const body = await dispatch(startUpdateSupportStatus(id, status));
    if (body && body.ok) {
      await loadPage(currentPageP || 0);
      if (detailOpen) {
        await dispatch(startLoadingInterestDetail(id));
      }
    }
  };

  const recordLabel = (row) => {
    const target = row.target;
    const id = (target && target.id) || row.target_reference_id || (row.support && row.support.reference_id);
    const name = target && target.name;
    if (!id) return '—';
    return name ? `#${id} — ${name}` : `#${id}`;
  };

  const renderStatusButtons = (row) => {
    if (!canWrite) return null;
    const current = row.status || (row.support && row.support.status) || 'nuevo';
    return (
      <div className="btn-group-vertical btn-group-sm">
        {current !== 'en_proceso' && (
          <button
            type="button"
            className="btn btn-outline-warning"
            onClick={() => onChangeStatus(row.id, 'en_proceso')}
          >
            {t('semanticSearch.inbox.markProcess', { defaultValue: 'En proceso' })}
          </button>
        )}
        {current !== 'cerrado' && (
          <button
            type="button"
            className="btn btn-outline-success"
            onClick={() => onChangeStatus(row.id, 'cerrado')}
          >
            {t('semanticSearch.inbox.markClosed', { defaultValue: 'Cerrar' })}
          </button>
        )}
        {current !== 'nuevo' && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => onChangeStatus(row.id, 'nuevo')}
          >
            {t('semanticSearch.inbox.markNew', { defaultValue: 'Reabrir' })}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="container mt-4">
      <h2>{t('semanticSearch.inbox.title', { defaultValue: 'Mis soportes' })}</h2>
      <p className="text-muted">
        {t('semanticSearch.inbox.subtitle', {
          defaultValue: 'Pedidos de contacto del Usuario Final. No es la bandeja del motor de flujos.',
        })}
      </p>
      {!canWrite && (
        <p className="alert alert-info">
          {t('semanticSearch.inbox.readOnlyHint', {
            defaultValue: 'Su rol puede consultar la bandeja. El cambio de estado lo hacen Gestor, Validador, Editor o Administrador.',
          })}
        </p>
      )}

      <form className="row g-2 align-items-end mb-3" onSubmit={onApplyFilters}>
        <div className="col-md-2">
          <label className="form-label">{t('semanticSearch.admin.module', { defaultValue: 'Módulo' })}</label>
          <select
            className="form-select"
            value={filters.module_key}
            onChange={(e) => setFilters({ ...filters, module_key: e.target.value })}
          >
            <option value="">{t('semanticSearch.admin.event.all', { defaultValue: 'Todos' })}</option>
            {MODULE_KEYS.map((k) => (
              <option key={k} value={k}>{moduleLabel(k)}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">{t('semanticSearch.admin.bridgeKind', { defaultValue: 'Origen' })}</label>
          <select
            className="form-select"
            value={filters.bridge_kind}
            onChange={(e) => setFilters({ ...filters, bridge_kind: e.target.value })}
          >
            <option value="">{t('semanticSearch.admin.event.all', { defaultValue: 'Todos' })}</option>
            <option value="dashboard">{t('semanticSearch.admin.kind.dashboard', { defaultValue: 'Panel' })}</option>
            <option value="view">{t('semanticSearch.admin.kind.view', { defaultValue: 'Ficha' })}</option>
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">{t('semanticSearch.admin.statusLabel', { defaultValue: 'Estado' })}</label>
          <select
            className="form-select"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">{t('semanticSearch.admin.event.all', { defaultValue: 'Todos' })}</option>
            <option value="nuevo">{t('semanticSearch.admin.status.nuevo', { defaultValue: 'Nuevo' })}</option>
            <option value="en_proceso">{t('semanticSearch.admin.status.en_proceso', { defaultValue: 'En proceso' })}</option>
            <option value="cerrado">{t('semanticSearch.admin.status.cerrado', { defaultValue: 'Cerrado' })}</option>
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">{t('semanticSearch.admin.from', { defaultValue: 'Desde' })}</label>
          <input
            type="date"
            className="form-control"
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label">{t('semanticSearch.admin.to', { defaultValue: 'Hasta' })}</label>
          <input
            type="date"
            className="form-control"
            value={filters.to}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label">&nbsp;</label>
          <button type="submit" className="btn btn-primary w-100" disabled={loading}>
            {t('semanticSearch.admin.apply', { defaultValue: 'Aplicar filtros' })}
          </button>
        </div>
        {excelExportEnabled && (
        <div className="col-md-2">
          <label className="form-label">&nbsp;</label>
          <button
            type="button"
            className="btn btn-outline-success w-100"
            disabled={loading || exporting}
            onClick={onExportExcel}
          >
            {exporting
              ? t('semanticSearch.inbox.exporting', { defaultValue: 'Preparando Excel…' })
              : t('semanticSearch.inbox.exportExcel', { defaultValue: 'Descargar Excel' })}
          </button>
        </div>
        )}
      </form>

      <div className="table-responsive">
        <table className="table table-striped table-hover align-middle">
          <thead>
            <tr>
              <th>{t('semanticSearch.inbox.col.received', { defaultValue: 'Recibido' })}</th>
              <th>{t('semanticSearch.inbox.col.module', { defaultValue: 'Módulo destino' })}</th>
              <th>{t('semanticSearch.inbox.col.record', { defaultValue: 'Registro' })}</th>
              <th>{t('semanticSearch.inbox.col.channel', { defaultValue: 'Canal / contacto' })}</th>
              <th>{t('semanticSearch.inbox.col.search', { defaultValue: 'Búsqueda' })}</th>
              <th>{t('semanticSearch.inbox.col.context', { defaultValue: 'Contexto' })}</th>
              <th>{t('semanticSearch.admin.statusLabel', { defaultValue: 'Estado' })}</th>
              <th>{t('semanticSearch.inbox.col.actions', { defaultValue: 'Acciones' })}</th>
            </tr>
          </thead>
          <tbody>
            {interestsP.length === 0 && (
              <tr>
                <td colSpan={8} className="text-muted">
                  {loading
                    ? t('semanticSearch.admin.loading', { defaultValue: 'Cargando…' })
                    : t('semanticSearch.inbox.empty', { defaultValue: 'No hay pedidos de soporte con estos filtros.' })}
                </td>
              </tr>
            )}
            {interestsP.map((row) => (
              <tr key={row.id}>
                <td>{formatDate(row.created_at)}</td>
                <td>{moduleLabel(row.module_key)}</td>
                <td className="small">{recordLabel(row)}</td>
                <td className="small">
                  {(row.support && row.support.contact_name) || row.contact_name || '—'}
                  <br />
                  {row.support
                    ? `${row.support.channel || ''} ${row.support.channel_value || ''}`.trim()
                    : '—'}
                </td>
                <td className="small">{row.search_text || (row.searchinfo && row.searchinfo.text_search) || '—'}</td>
                <td className="small">
                  {t(`semanticSearch.admin.kind.${row.bridge_kind || 'view'}`, { defaultValue: row.bridge_kind || '—' })}
                  <br />
                  {formatOrigins(row)}
                </td>
                <td>{t(`semanticSearch.admin.status.${row.status || 'nuevo'}`, { defaultValue: row.status || 'nuevo' })}</td>
                <td>
                  <button type="button" className="btn btn-sm btn-outline-primary mb-1" onClick={() => onOpenDetail(row)}>
                    {t('semanticSearch.admin.open', { defaultValue: 'Abrir' })}
                  </button>
                  {renderStatusButtons(row)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <SimpleSelect listOptions={listOptions} onChangeSelect={onChangeSelect} pageSize={itemsPerPage} />
      </div>
      <div className="pagination justify-content-center">
        <Pagination
          totalPages={totalPagesP}
          totalItems={totalItemsP}
          currentPage={currentPageP + 1}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      </div>

      <Modal isOpen={detailOpen} onRequestClose={onCloseDetail} style={modalStyles} contentLabel="Detalle soporte">
        {!selectedInterest ? (
          <p>{t('semanticSearch.admin.loading', { defaultValue: 'Cargando…' })}</p>
        ) : (
          <>
            <h5 className="mb-3">
              {t('semanticSearch.inbox.detailTitle', { defaultValue: 'Atender pedido' })} #{selectedInterest.id}
            </h5>
            <h6>{t('semanticSearch.admin.layer1', { defaultValue: '1. Búsqueda' })}</h6>
            <p>
              {selectedInterest.searchinfo
                ? `#${selectedInterest.searchinfo.id} — ${selectedInterest.searchinfo.text_search || '—'}`
                : (selectedInterest.search_text || t('semanticSearch.admin.noSearch', { defaultValue: 'Sin búsqueda asociada' }))}
            </p>
            <h6>{t('semanticSearch.admin.layer2', { defaultValue: '2. Intención' })}</h6>
            <p>
              {t('semanticSearch.admin.kindLabel', { defaultValue: 'Contexto' })}:{' '}
              <strong>{selectedInterest.bridge_kind || '—'}</strong>
            </p>
            <p>
              {t('semanticSearch.admin.origins', { defaultValue: 'Orígenes' })}
            </p>
            <ul>
              {(selectedOrigins || []).length
                ? selectedOrigins.map((o) => (
                  <li key={`${o.module_key}-${o.id}`}>
                    {o.module_key} #{o.id}{o.name ? ` (${o.name})` : ''}
                    {' '}
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0"
                      onClick={() => openRecord(o.module_key, o.id)}
                    >
                      {t('semanticSearch.inbox.openOrigin', { defaultValue: 'Abrir origen' })}
                    </button>
                  </li>
                ))
                : <li>—</li>}
            </ul>
            <p>
              {t('semanticSearch.admin.target', { defaultValue: 'Destino' })}:{' '}
              {selectedTarget
                ? `${selectedTarget.module_key} #${selectedTarget.id}${selectedTarget.name ? ` (${selectedTarget.name})` : ''}`
                : `${moduleLabel(selectedInterest.module_key)} #${selectedInterest.target_reference_id || '—'}`}
              {' '}
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => openRecord(
                  (selectedTarget && selectedTarget.module_key) || selectedInterest.module_key,
                  (selectedTarget && selectedTarget.id) || selectedInterest.target_reference_id
                )}
              >
                {t('semanticSearch.inbox.openTarget', { defaultValue: 'Abrir ficha destino' })}
              </button>
            </p>
            <h6>{t('semanticSearch.admin.layer3', { defaultValue: '3. Soporte / contacto' })}</h6>
            {selectedInterest.support ? (
              <ul>
                <li>
                  {selectedInterest.support.contact_name} — {selectedInterest.support.channel}{' '}
                  {selectedInterest.support.channel_value}
                </li>
                <li>
                  {t('semanticSearch.admin.said', { defaultValue: 'Respuesta' })}:{' '}
                  {selectedInterest.support.main_interest_code || '—'}
                </li>
                {listSupportAnswers(selectedInterest.support).map((a) => (
                  <li key={a.id || a.key || a.question_key}>
                    {formatSupportAnswerLabel(a)}: {formatSupportAnswerValue(a)}
                  </li>
                ))}
                {selectedInterest.support.comments
                  && !listSupportAnswers(selectedInterest.support).some((a) => a.key === 'comment') && (
                  <li>{selectedInterest.support.comments}</li>
                )}
                <li>
                  {t('semanticSearch.admin.statusLabel', { defaultValue: 'Estado' })}:{' '}
                  {t(`semanticSearch.admin.status.${selectedInterest.status || 'nuevo'}`, {
                    defaultValue: selectedInterest.status || 'nuevo',
                  })}
                </li>
              </ul>
            ) : (
              <p className="text-muted">
                {t('semanticSearch.admin.noSupport', { defaultValue: 'Este evento no es un pedido de contacto.' })}
              </p>
            )}
            {canWrite && selectedInterest && (
              <div className="mb-3">
                {renderStatusButtons(selectedInterest)}
              </div>
            )}
            <div className="text-end">
              <button type="button" className="btn btn-light" onClick={onCloseDetail}>
                {t('buttons.close', { defaultValue: 'Cerrar' })}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default SpecialistSupportInbox;
